/**
 * Protests, closures and crowds in the departure window.
 *
 * The only forward-looking dataset in the planner, and the only one where the
 * departure time changes the answer rather than decorating it. Events we drive
 * past before or after are kept but set aside, so the operator can see they
 * were considered and ruled out rather than never found.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { DistanceUnits, RouteEvent, RouteEventsReport } from '../types';
import { formatDistance } from '../geo/format';
import { fromLocalInput, toLocalInput } from '../eventWindow';
import { IntelSection } from './intel';
import {
  DockAction,
  EmptyState,
  ErrorLine,
  GOLD,
  Loading,
  Provenance,
  TabHeader,
  WARN,
} from './DockPrimitives';

/** Same day gets a time range; anything further out needs the day named. */
function formatWindow(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime())) return '';
  const time = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const today = new Date().toDateString() === start.toDateString();
  const day = today ? '' : `${start.toLocaleDateString([], { weekday: 'short' })} `;
  if (Number.isNaN(end.getTime()) || start.toDateString() !== end.toDateString()) {
    return `${day}${time(start)}`;
  }
  return `${day}${time(start)}–${time(end)}`;
}

function sourceLabel(event: RouteEvent): string {
  if (event.source_title) return event.source_title;
  try {
    return new URL(event.source_url).hostname;
  } catch {
    return 'source';
  }
}

const EventRow: React.FC<{
  event: RouteEvent;
  units: DistanceUnits;
  onFocus?: () => void;
  muted?: boolean;
}> = ({ event, units, onFocus, muted }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, py: 0.35, opacity: muted ? 0.6 : 1 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: '0.64rem', width: 46, flexShrink: 0, textAlign: 'right' }}
    >
      {formatDistance(event.distance_along_m, units)}
    </Typography>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        role={onFocus ? 'button' : undefined}
        tabIndex={onFocus ? 0 : -1}
        onClick={onFocus}
        onKeyDown={(e) => { if (e.key === 'Enter') onFocus?.(); }}
        sx={{
          display: 'block',
          fontWeight: 600,
          cursor: onFocus ? 'pointer' : 'default',
          '&:hover': onFocus ? { textDecoration: 'underline' } : undefined,
          '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
        }}
      >
        {event.title}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', color: 'text.secondary' }}>
        {event.label} · {formatWindow(event.starts_at, event.ends_at)}
        {event.offset_m < 100 ? ' · on route' : ` · ${formatDistance(event.offset_m, units)} off`}
        {event.confidence === 'low' && ' · unconfirmed'}
      </Typography>
      {event.summary && (
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', color: 'text.secondary' }}>
          {event.summary}
        </Typography>
      )}
      <Link
        href={event.source_url}
        target="_blank"
        rel="noopener noreferrer"
        variant="caption"
        sx={{ fontSize: '0.6rem', color: 'text.disabled', '&:hover': { color: GOLD } }}
      >
        {sourceLabel(event)}
      </Link>
    </Box>
  </Box>
);

export interface EventsTabProps {
  section: IntelSection<RouteEventsReport>;
  units: DistanceUnits;
  hasRoute: boolean;
  hasDepartAt: boolean;
  corridorM?: number;
  onCorridorChange?: (metres: number) => void;
  horizonHours?: number;
  onHorizonChange?: (hours: number) => void;
  windowStart?: string;
  windowEnd?: string;
  onWindowStartChange?: (iso: string) => void;
  onWindowEndChange?: (iso: string) => void;
  onFocusEvent?: (event: RouteEvent) => void;
}

const EventsTab: React.FC<EventsTabProps> = ({
  section,
  units,
  hasRoute,
  hasDepartAt,
  corridorM,
  onCorridorChange,
  horizonHours = 72,
  onHorizonChange,
  windowStart,
  windowEnd,
  onWindowStartChange,
  onWindowEndChange,
  onFocusEvent,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [missedOpen, setMissedOpen] = useState(false);

  const report = section.data;

  const { live, missed } = useMemo(() => {
    const all = report?.events || [];
    return {
      live: all.filter((e) => e.overlaps_transit !== false),
      missed: all.filter((e) => e.overlaps_transit === false),
    };
  }, [report]);

  const search = () => {
    if (section.enabled) section.refresh?.();
    else section.setEnabled?.(true);
  };
  const canSearch = hasRoute && (!!section.setEnabled || !!section.refresh);
  const corridor = corridorM ?? report?.corridor_m ?? 1500;
  const worst = live.reduce((max, e) => Math.max(max, e.impact), 0);

  return (
    <Box>
      <TabHeader
        title="Events"
        hint={report
          ? (corridor >= 1000 ? `${(corridor / 1000).toFixed(1)} km` : `${corridor} m`)
          : undefined}
        actions={(
          <>
            {onCorridorChange && (
              <DockAction onClick={() => setSettingsOpen(true)} title="Radius and how far ahead to look">
                Scope
              </DockAction>
            )}
            {canSearch && (
              <DockAction onClick={search} disabled={section.loading}>
                {report ? 'Search again' : 'Search'}
              </DockAction>
            )}
          </>
        )}
      />

      {onWindowStartChange && onWindowEndChange && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            label="From"
            type="datetime-local"
            size="small"
            value={toLocalInput(windowStart)}
            onChange={(e) => {
              const next = fromLocalInput(e.target.value);
              if (next) onWindowStartChange(next);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, '& input': { fontSize: '0.72rem' } }}
          />
          <TextField
            label="To"
            type="datetime-local"
            size="small"
            value={toLocalInput(windowEnd)}
            onChange={(e) => {
              const next = fromLocalInput(e.target.value);
              if (next) onWindowEndChange(next);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, '& input': { fontSize: '0.72rem' } }}
          />
        </Box>
      )}

      {section.loading && <Loading label="Searching local sources…" />}
      {section.error && <ErrorLine>{section.error}</ErrorLine>}

      {!report && !section.loading && (
        <EmptyState>
          {hasRoute
            ? 'Protests, closures and crowds near this route, read out of local reporting. Results stay here once searched.'
            : 'Plan a route to search for disruptions along it.'}
        </EmptyState>
      )}

      {report && !report.dataset.available && (
        <>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.66rem' }}>
            {report.dataset.note || 'Event search is unavailable right now.'}
          </Typography>
          {report.queries.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', color: 'text.disabled' }}>
                Searches this route would run:
              </Typography>
              {report.queries.map((query) => (
                <Typography
                  key={query}
                  variant="caption"
                  sx={{ display: 'block', fontSize: '0.62rem', color: 'text.disabled', pl: 1 }}
                >
                  {query}
                </Typography>
              ))}
            </Box>
          )}
        </>
      )}

      {report && report.dataset.available && (
        <>
          {!hasDepartAt && report.events.length > 0 && (
            <Typography variant="caption" sx={{ display: 'block', color: WARN, fontSize: '0.64rem' }}>
              Set a departure time to rule out events you would miss.
            </Typography>
          )}

          {report.events.length === 0 && (
            <EmptyState>
              Nothing reported near this route in that window.
            </EmptyState>
          )}

          {live.length > 0 && (
            <Box sx={{ mt: 0.25 }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', fontWeight: 600, color: worst >= 0.7 ? WARN : 'text.primary' }}
              >
                {live.length} event{live.length === 1 ? '' : 's'}
                {hasDepartAt ? ' while you are on the road' : ' in the window'}
              </Typography>
              {live.map((event) => (
                <EventRow
                  key={`${event.source_url}-${event.starts_at}`}
                  event={event}
                  units={units}
                  onFocus={onFocusEvent ? () => onFocusEvent(event) : undefined}
                />
              ))}
            </Box>
          )}

          {missed.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Box
                onClick={() => setMissedOpen(!missedOpen)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setMissedOpen(!missedOpen); }}
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  cursor: 'pointer',
                  py: 0.25,
                  '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {missed.length} more you pass before or after
                </Typography>
                <Typography variant="caption" sx={{ color: GOLD, fontSize: '0.64rem' }}>
                  {missedOpen ? 'Hide' : 'Show'}
                </Typography>
              </Box>
              {missedOpen && missed.map((event) => (
                <EventRow
                  key={`${event.source_url}-${event.starts_at}`}
                  event={event}
                  units={units}
                  onFocus={onFocusEvent ? () => onFocusEvent(event) : undefined}
                  muted
                />
              ))}
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem', color: 'text.disabled' }}
          >
            Web search read by Sentinel — check the source before acting
            {report.cached ? ' · cached' : ''}
          </Typography>
        </>
      )}

      <Provenance section={section} onRefresh={canSearch ? search : undefined} />

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Events on the way</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1.5 }}>
            Search radius — {corridor >= 1000 ? `${(corridor / 1000).toFixed(1)} km` : `${corridor} m`} either side
          </Typography>
          <Slider
            size="small"
            value={corridor}
            min={250}
            max={5000}
            step={250}
            marks={[{ value: 250, label: '250 m' }, { value: 5000, label: '5 km' }]}
            valueLabelDisplay="auto"
            onChange={(_, value) => onCorridorChange?.(value as number)}
            sx={{ color: GOLD, mb: 2 }}
          />
          {onWindowStartChange && onWindowEndChange && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                Route window
              </Typography>
              <TextField
                label="From"
                type="datetime-local"
                size="small"
                fullWidth
                value={toLocalInput(windowStart)}
                onChange={(e) => {
                  const next = fromLocalInput(e.target.value);
                  if (next) onWindowStartChange(next);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.25 }}
              />
              <TextField
                label="To"
                type="datetime-local"
                size="small"
                fullWidth
                value={toLocalInput(windowEnd)}
                onChange={(e) => {
                  const next = fromLocalInput(e.target.value);
                  if (next) onWindowEndChange(next);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 1.5 }}
              />
            </>
          )}
          {onHorizonChange && !onWindowStartChange && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1.5 }}>
                Look ahead — {Math.round(horizonHours / 24)} day{horizonHours >= 48 ? 's' : ''}
              </Typography>
              <Slider
                size="small"
                value={horizonHours}
                min={24}
                max={336}
                step={24}
                marks={[{ value: 24, label: '1 d' }, { value: 336, label: '14 d' }]}
                valueLabelDisplay="auto"
                onChange={(_, value) => onHorizonChange(value as number)}
                sx={{ color: GOLD, mb: 1.5 }}
              />
            </>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Results come from a web search that Sentinel reads and structures. Every event keeps the
            link it came from, and anything that could not be traced to a retrieved page is dropped
            rather than shown.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            From and to are the estimated time on this route. Events outside that window stay in
            the list but are set aside.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EventsTab;
